import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, LinearProgress, Table, TableBody,
  TableCell, TableHead, TableRow, CircularProgress,
} from '@mui/material';

const API = process.env.REACT_APP_API_BASE_URL;
const BID = process.env.REACT_APP_DECORMANAGER_BUILDER_ID;

const UsageDetail = ({ open, onClose, apiKey }) => {
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!open || !apiKey) return;
    setLoading(true);

    const fetchUsage = async () => {
      try {
        const token = await window.firebaseAuth.currentUser.getIdToken();
        const resp = await fetch(
          `${API}/auth/external-api-keys/usage?builder_id=${BID}&authorization=${token}&key_id=${apiKey.id}`,
        );
        const result = await resp.json();
        if (result.success) {
          setUsage(result.data.usage);
        }
      } catch (e) {
        console.error('Failed to fetch usage', e);
      }
      setLoading(false);
    };

    fetchUsage();
  }, [open, apiKey]);

  if (!apiKey) return null;

  const currentMonthData = usage[0];
  const quotaPercent = apiKey.monthlyLimit && currentMonthData
    ? Math.min(100, (currentMonthData.requestCount / apiKey.monthlyLimit) * 100)
    : 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Usage:
        {' '}
        {apiKey.label}
        {' '}
        (
        {apiKey.keyPrefix}
        ...
        )
      </DialogTitle>
      <DialogContent>
        {
loading ? (
  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
    <CircularProgress />
  </Box>
) : (
  <>
    {/* Current month summary */}
    <Box sx={
{
  mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1,
}
}
    >
      <Typography variant="subtitle2" gutterBottom>Current Month</Typography>
      <Typography variant="h4">
        {currentMonthData?.requestCount || 0}
        {
apiKey.monthlyLimit && (
  <Typography component="span" variant="body1" color="text.secondary">
    {' '}
    /
    {' '}
    {apiKey.monthlyLimit.toLocaleString()}
  </Typography>
)
}
      </Typography>
      {
apiKey.monthlyLimit && (
  <LinearProgress
    variant="determinate"
    value={quotaPercent}
    // eslint-disable-next-line no-nested-ternary
    color={quotaPercent > 90 ? 'error' : quotaPercent > 70 ? 'warning' : 'primary'}
    sx={{ mt: 1, height: 8, borderRadius: 4 }}
  />
)
}
    </Box>

    {/* Daily breakdown bar chart */}
    {
currentMonthData?.byDay && (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle2" gutterBottom>Daily Breakdown</Typography>
    <Box sx={
{
  display: 'flex', alignItems: 'flex-end', gap: '2px', height: 100,
}
}
    >
      {
Object.entries(currentMonthData.byDay)
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([day, count]) => {
    const maxCount = Math.max(...Object.values(currentMonthData.byDay));
    const height = maxCount > 0 ? (count / maxCount) * 100 : 0;
    return (
      <Box
        key={day}
        sx={
{
  flex: 1,
  bgcolor: 'primary.main',
  height: `${Math.max(2, height)}%`,
  borderRadius: '2px 2px 0 0',
  minWidth: 4,
}
}
        title={`${day}: ${count} requests`}
      />
    );
  })
}
    </Box>
  </Box>
)
}

    {/* Endpoint breakdown */}
    {
currentMonthData?.byEndpoint && (
  <Box sx={{ mb: 3 }}>
    <Typography variant="subtitle2" gutterBottom>Endpoint Breakdown</Typography>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Endpoint</TableCell>
          <TableCell align="right">Requests</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {
Object.entries(currentMonthData.byEndpoint)
  .sort(([, a], [, b]) => b - a)
  .map(([endpoint, count]) => (
    <TableRow key={endpoint}>
      <TableCell sx={{ fontFamily: 'monospace', fontSize: 13 }}>{endpoint}</TableCell>
      <TableCell align="right">{count}</TableCell>
    </TableRow>
  ))
}
      </TableBody>
    </Table>
  </Box>
)
}

    {/* Monthly history */}
    {
usage.length > 1 && (
  <Box>
    <Typography variant="subtitle2" gutterBottom>Monthly History</Typography>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Month</TableCell>
          <TableCell align="right">Total Requests</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {
usage.map((u) => (
  <TableRow key={u.month}>
    <TableCell>{u.month}</TableCell>
    <TableCell align="right">{u.requestCount?.toLocaleString()}</TableCell>
  </TableRow>
))
}
      </TableBody>
    </Table>
  </Box>
)
}

    {
usage.length === 0 && (
  <Typography color="text.secondary">No usage data yet.</Typography>
)
}
  </>
)
}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default UsageDetail;
